import { useState, useEffect, useCallback } from 'react';
import { statsAPI } from '@/services/api';
import type { DashboardStats } from '@/types/domain';

/**
 * useDashboardStats Hook 返回值类型
 */
export interface UseDashboardStatsReturn {
  /** 统计数据 */
  stats: DashboardStats | null;
  /** 加载状态 */
  loading: boolean;
  /** 当前时间窗口（天数） */
  timeWindow: number;
  /** 设置时间窗口 */
  setTimeWindow: (days: number) => void;
  /** 刷新数据 */
  refresh: () => void;
}

/**
 * Dashboard 统计数据 Hook
 *
 * 管理 Dashboard 页面的统计数据获取和时间窗口选择。
 *
 * @example
 * const { stats, loading, timeWindow, setTimeWindow } = useDashboardStats();
 */
export function useDashboardStats(): UseDashboardStatsReturn {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeWindow, setTimeWindow] = useState(7);

  // 加载统计数据
  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await statsAPI.get({ activeDevicesDays: timeWindow });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  }, [timeWindow]);

  // 时间窗口变化时重新加载
  useEffect(() => {
    let isActive = true;

    const fetchStats = async () => {
      setLoading(true);
      try {
        const response = await statsAPI.get({ activeDevicesDays: timeWindow });
        if (isActive) {
          setStats(response.data);
        }
      } catch (error) {
        console.error('Failed to load stats:', error);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    void fetchStats();

    return () => {
      isActive = false;
    };
  }, [timeWindow]);

  // 刷新数据
  const refresh = useCallback(() => {
    void loadStats();
  }, [loadStats]);

  return {
    stats,
    loading,
    timeWindow,
    setTimeWindow,
    refresh,
  };
}
